import {
  Paper,
  Typography,
  Box,
  Avatar,
  Alert,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  Star as StarIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  EmojiEvents as TrophyIcon,
} from "@mui/icons-material";

const AchievementItem = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textAlign: "center",
  padding: theme.spacing(2),
  borderRadius: 8,
  backgroundColor: "#fafafa",
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1.5),
  },
}));

const AchievementIcon = styled(Avatar)(({ theme, bgcolor }) => ({
  width: 48,
  height: 48,
  backgroundColor: bgcolor,
  marginBottom: theme.spacing(1),
  [theme.breakpoints.down("sm")]: {
    width: 40,
    height: 40,
  },
}));

function Achievements({ leaderBoardData }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const achievements = [
    {
      id: 1,
      title: "Rising Star",
      backgroundColor: "#E0E1FD",
      iconColor: "#6366F1",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-8 w-8 text-blue-600"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
          ></path>
        </svg>
      ),
      achieved: leaderBoardData?.raisingStarBadge?.achieved,
    },
    {
      id: 2,
      title: "90%+ Resolution",
      backgroundColor: "rgb(220 252 231 / var(--tw-bg-opacity, 1))",
      iconColor: "rgb(22 163 74 / var(--tw-text-opacity, 1))",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-8 w-8 text-green-600"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
          ></path>
        </svg>
      ),
      achieved: leaderBoardData?.resolutionBadge?.achieved,
    },
    {
      id: 3,
      title: "Consistency",
      backgroundColor: "rgb(243 232 255 / var(--tw-bg-opacity, 1))",
      iconColor: "rgb(147 51 234 / var(--tw-text-opacity, 1))",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-8 w-8 text-purple-600"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
          ></path>
        </svg>
      ),
      achieved: leaderBoardData?.ConsistencyBadge?.achieved,
    },
    {
      id: 4,
      title: "Customer Champian",
      backgroundColor: "rgb(219 234 254 / var(--tw-bg-opacity, 1))",
      iconColor: "rgb(37 99 235 / var(--tw-text-opacity, 1))",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-8 w-8 text-blue-600"
          fill="currentColor"
          viewBox="0 0 24 24"
        >
          <path d="M3 4l4 4 5-5 5 5 4-4v13H3V4zm2 11h14v2H5v-2z" />
        </svg>
      ),
      achieved: leaderBoardData?.csatBadge?.achieved,
    },
  ];

  const achievedBadges = achievements.filter((a) => a.achieved);
  const remainingBadges = achievements.filter((a) => !a.achieved);

  return (
    <Paper
      elevation={0}
      sx={{ p: 3, borderRadius: 3, border: "1px solid #e0e0e0" }}
    >
      <Typography
        variant={isMobile ? "h6" : "h6"}
        component="h3"
        fontWeight="600"
        color="text.primary"
        gutterBottom
        sx={{ fontSize: "1.125rem" }}
      >
        Your Achievements
      </Typography>

      {achievedBadges.length > 0 ? (
        <>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: 2,
              mt: 3,
              mb: 3,
              gridTemplateColumns:
                achievedBadges.length === 1
                  ? "1fr"
                  : achievedBadges.length === 3
                  ? "repeat(2, 1fr)"
                  : "repeat(2, 1fr)",
            }}
          >
            {achievedBadges.map((achievement, index) => (
              <AchievementItem key={index}>
                <AchievementIcon bgcolor={achievement?.backgroundColor}>
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      color: achievement?.iconColor,
                    }}
                  >
                    {achievement?.icon}
                  </Box>
                </AchievementIcon>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    fontSize: isMobile ? "0.7rem" : "0.75rem",
                    fontWeight: 500,
                  }}
                >
                  {achievement?.title}
                </Typography>
              </AchievementItem>
            ))}
          </Box>

          {remainingBadges.length > 0 && (
            <Alert
              icon={<TrophyIcon />}
              severity="info"
              sx={{
                backgroundColor: "#FEFCE8",
                color: "#EAB308",
                border: "1px solid #ffcc02",
                borderRadius: 2,
                "& .MuiAlert-icon": {
                  color: "#EAB308",
                },
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontSize: isMobile ? "0.8rem" : "0.875rem" }}
              >
                <strong>You're doing great!</strong>
                <br />
                {remainingBadges.length === 3 &&
                  "You're off to a good start. Earn more badges by staying consistent!"}
                {remainingBadges.length === 2 &&
                  "Halfway there! Unlock your remaining achievements!"}
                {remainingBadges.length === 1 &&
                  `Almost complete! Just one more badge to go: ${remainingBadges[0].title}.`}
              </Typography>
            </Alert>
          )}
        </>
      ) : (
        <Alert
          icon={<TrophyIcon />}
          severity="info"
          sx={{
            mt: 3,
            backgroundColor: "#FEFCE8",
            color: "#EAB308",
            border: "1px solid #ffcc02",
            borderRadius: 2,
            "& .MuiAlert-icon": {
              color: "#EAB308",
            },
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontSize: isMobile ? "0.8rem" : "0.875rem" }}
          >
            <strong>Keep going!</strong>
            <br />
            Start achieving your first badge by staying consistent and resolving
            tickets effectively.
          </Typography>
        </Alert>
      )}

      {/* CSAT improvement alert remains always */}
      {/* <Alert
        icon={<TrophyIcon />}
        severity="info"
        sx={{
          backgroundColor: "#FEFCE8",
          color: "#EAB308",
          border: "1px solid #ffcc02",
          borderRadius: 2,
          mt: 2,
          "& .MuiAlert-icon": {
            color: "#EAB308",
          },
        }}
      >
        <Typography
          variant="body2"
          sx={{ fontSize: isMobile ? "0.8rem" : "0.875rem" }}
        >
          <strong>You're close to unlocking the Customer Champion badge!</strong>
          <br />
          Improve your CSAT score to 4.5+ to earn this achievement.
        </Typography>
      </Alert> */}
    </Paper>
  );
}

export default Achievements;
